import { Controller } from "@hotwired/stimulus"

// usage
/*
  <div data-controller="makase-effect"
       data-makase-effect-mount-value="console.log('mounted', $el, this)">
    <button>클릭</button>
  </div>

  <!-- 고급 사용법 -->
  <div data-controller="makase-effect"
       data-makase-effect-mount-value="
         window.xBaseKit(({ downflow })=>{
           downflow($el, {
             onResolve: ()=>{ this.dispatch('close') },
             onReject: ()=>{ this.dispatch('close') }
           }, 'modal_staff_profile_cell')
         })
       "
       data-makase-effect-unmount-value="console.log('unmounting')">
    <button>클릭</button>
  </div>

  <!-- 이벤트를 통한 제어 -->
  element.dispatchEvent(new CustomEvent("makase-effect:execute"))
  element.dispatchEvent(new CustomEvent("makase-effect:execute-mount"))
  element.dispatchEvent(new CustomEvent("makase-effect:execute-unmount"))
*/

export default class MakaseEffectController extends Controller {
  // this.mountValue : string // mount 시 실행할 JavaScript 코드
  // this.unmountValue : string // unmount 시 실행할 JavaScript 코드
  static values = {
    mount: { type: String, default: "" },
    unmount: { type: String, default: "" },
  }

  connect() {
    // 이벤트 리스너 바인딩
    this.boundExecute = this.execute.bind(this)
    this.boundExecuteMount = this.executeMount.bind(this)
    this.boundExecuteUnmount = this.executeUnmount.bind(this)

    // 커스텀 이벤트 리스너
    this.element.addEventListener("makase-effect:execute", this.boundExecute)
    this.element.addEventListener("makase-effect:execute-mount", this.boundExecuteMount)
    this.element.addEventListener("makase-effect:execute-unmount", this.boundExecuteUnmount)

    // mount 시 자동 실행
    if (this.mountValue) {
      this.executeMount()
    }
  }

  disconnect() {
    // unmount 시 자동 실행
    if (this.unmountValue) {
      this.executeUnmount()
    }

    this.element.removeEventListener("makase-effect:execute", this.boundExecute)
    this.element.removeEventListener("makase-effect:execute-mount", this.boundExecuteMount)
    this.element.removeEventListener("makase-effect:execute-unmount", this.boundExecuteUnmount)
  }

  execute(event) {
    event?.stopPropagation?.()
    this.executeMount()
  }

  executeMount(event) {
    event?.stopPropagation?.()

    if (!this.mountValue) return

    try {
      // petite-vue 스타일: $el은 현재 element를 가리킴
      // this는 stimulus controller를 가리킴
      const $el = this.element
      const controller = this

      // 코드 실행 컨텍스트 생성
      const executeCode = new Function('$el', 'controller', `
        // this를 controller로 바인딩하여 실행
        return (function() {
          ${this.mountValue}
        }).call(controller);
      `)

      executeCode($el, controller)

      // 실행 완료 이벤트
      this.element.dispatchEvent(new CustomEvent("makase-effect:mount-executed", {
        bubbles: true,
        detail: { element: this.element }
      }))

    } catch (error) {
      console.error("MakaseEffect mount execution error:", error)

      // 에러 이벤트
      this.element.dispatchEvent(new CustomEvent("makase-effect:error", {
        bubbles: true,
        detail: { error, type: "mount" }
      }))
    }
  }

  executeUnmount(event) {
    event?.stopPropagation?.()

    if (!this.unmountValue) return

    try {
      const $el = this.element
      const controller = this

      const executeCode = new Function('$el', 'controller', `
        return (function() {
          ${this.unmountValue}
        }).call(controller);
      `)

      executeCode($el, controller)

      // 실행 완료 이벤트
      this.element.dispatchEvent(new CustomEvent("makase-effect:unmount-executed", {
        bubbles: true,
        detail: { element: this.element }
      }))

    } catch (error) {
      console.error("MakaseEffect unmount execution error:", error)

      // 에러 이벤트
      this.element.dispatchEvent(new CustomEvent("makase-effect:error", {
        bubbles: true,
        detail: { error, type: "unmount" }
      }))
    }
  }

  // Stimulus actions를 위한 메서드
  close() {
    // close 액션이 호출되면 close 이벤트 발생
    this.element.dispatchEvent(new CustomEvent("makase-effect:close", {
      bubbles: true
    }))
  }

  resolve(value) {
    this.element.dispatchEvent(new CustomEvent("makase-effect:resolve", {
      bubbles: true,
      detail: { value }
    }))
  }

  reject(reason) {
    this.element.dispatchEvent(new CustomEvent("makase-effect:reject", {
      bubbles: true,
      detail: { reason }
    }))
  }

  // 외부에서 호출 가능한 헬퍼 메서드
  dispatch(eventName, detail = {}) {
    this.element.dispatchEvent(new CustomEvent(`makase-effect:${eventName}`, {
      bubbles: true,
      detail
    }))
  }

  // 값 변경 감지
  mountValueChanged() {
    // mount 값이 변경되면 자동 실행하지 않음 (명시적 호출만)
  }

  unmountValueChanged() {
    // unmount 값이 변경되어도 자동 실행하지 않음
  }
}
